import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip as ChartTooltip,
  Legend as ChartLegend,
  ChartData,
  ChartOptions,
  TooltipItem,
} from 'chart.js';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAuth } from './AuthContext';

/* ------------------------------------------------------------------
 * Register core Chart.js elements
 * ------------------------------------------------------------------ */
import ChartDataLabels from 'chartjs-plugin-datalabels';

ChartJS.register(ArcElement, ChartTooltip, ChartLegend, ChartDataLabels);

/* ------------------------------------------------------------------
 * Props & Types
 * ------------------------------------------------------------------ */
export interface PieChartProps {
  /** "android" | "ios" */
  dataType: string;
  /** notify parent when we’ve rendered / finished processing */
  onLoaded?: () => void;
  /** collapse slices below this % into "Other" (0 disables) */
  minSlicePercent?: number;
  /** max slices before collapsing remainder into "Other" */
  maxSlices?: number;
  /** Optional: Direct data injection to bypass AuthContext/appData */
  preCalculatedCounts?: Record<string, number>;
  /** Always render this label as a discrete slice, even if it falls under
   *  minSlicePercent or beyond maxSlices. Used to keep the latest release
   *  visible when only a handful of apps have rolled forward. */
  pinnedLabel?: string;
}

interface AppDataEntry {
  android_version: string | null;
  ios_version: string | null;
  appdata: { id: number; name: string };
  branch: string;
  domain: string;
  triggerData: { triggerData: any[] };
  user: string;
}
interface InputObject {
  [key: string]: AppDataEntry;
}
interface VersionCounts {
  android_version: Record<string, number>;
  ios_version: Record<string, number>;
}

/* ------------------------------------------------------------------
 * Colors
 * ------------------------------------------------------------------ */
const DEFAULT_COLORS = [
  '#6366f1', // Indigo
  '#8b5cf6', // Purple
  '#ec4899', // Pink
  '#ef4444', // Red
  '#f97316', // Orange
  '#f59e0b', // Amber
  '#84cc16', // Lime
  '#10b981', // Emerald
  '#14b8a6', // Teal
  '#06b6d4', // Cyan
  '#3b82f6', // Blue
  '#a855f7', // Violet
  '#f43f5e', // Rose
  '#78716c', // Stone
  '#64748b', // Slate
];


/* ------------------------------------------------------------------
 * Component
 * ------------------------------------------------------------------ */
export const PieChart: React.FC<PieChartProps> = (props) => {
  const {
    dataType,
    onLoaded,
    minSlicePercent = 1,
    maxSlices = 12,
  } = props;
  const { appData } = useAuth();
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState<ChartData<'pie'>>({ labels: [], datasets: [] });
  const [error, setError] = useState<string | null>(null);
  const chartRef = useRef<any>(null);

  /* --------------------------------------------------------------
   * Aggregate versions (memoized)
   * -------------------------------------------------------------- */
  const versionCounts: VersionCounts = useMemo(() => {
    // 1. If pre-calculated data is provided (e.g. from Analytics Dashboard), use it directly
    if (props.preCalculatedCounts) {
      const counts = props.preCalculatedCounts;
      // We only populate the key matching 'dataType' for safety, though the parent should pass the right one.
      return {
        android_version: props.dataType === 'android' ? counts : {},
        ios_version: props.dataType === 'ios' ? counts : {}
      };
    }

    const input = (appData ?? {}) as InputObject;
    const result: VersionCounts = { android_version: {}, ios_version: {} };

    // Processing count was previously logged on every re-render; that
    // dominated the DevTools console without surfacing any actionable
    // signal. The dropoff/empty cases below are still logged.

    for (const key in input) {
      const entry = input[key];
      if (!entry) continue;

      // Extract platform: prefer explicit 'platform' field from backend
      const entryPlatform = (entry as any).platform?.toLowerCase();

      // Only count Android versions if it's an Android app
      const hasAndroidData = entry.android_version !== undefined && entry.android_version !== null;
      if ((entryPlatform === 'android' || (!entryPlatform && hasAndroidData)) && hasAndroidData) {
        const av = entry.android_version || 'N/A';
        result.android_version[av] = (result.android_version[av] || 0) + 1;
      }

      // Only count iOS versions if it's an iOS app
      const hasIosData = entry.ios_version !== undefined && entry.ios_version !== null;
      if ((entryPlatform === 'ios' || (!entryPlatform && hasIosData)) && hasIosData) {
        const iv = entry.ios_version || 'N/A';
        result.ios_version[iv] = (result.ios_version[iv] || 0) + 1;
      }
    }

    // Remove empty entries (where count is 0)
    const cleanAndSort = (versions: Record<string, number>) => {
      const filtered = Object.entries(versions).filter(([, count]) => count > 0);

      // Sort: N/A last, then by version number
      filtered.sort(([a], [b]) => {
        if (a === 'N/A') return 1;
        if (b === 'N/A') return -1;
        // Natural sort for version numbers
        return a.localeCompare(b, undefined, { numeric: true });
      });

      return Object.fromEntries(filtered);
    };

    result.android_version = cleanAndSort(result.android_version);
    result.ios_version = cleanAndSort(result.ios_version);

    return result;
  }, [appData, props.preCalculatedCounts, props.dataType]);

  /* --------------------------------------------------------------
   * Build chart dataset (slice collapse + color map)
   * -------------------------------------------------------------- */
  const buildChartData = (
    counts: Record<string, number>,
    palette = DEFAULT_COLORS,
    minPct = minSlicePercent,
    max = maxSlices,
    pinned = props.pinnedLabel
  ): ChartData<'pie'> => {
    const entries = Object.entries(counts).filter(([, count]) => count > 0);
    const total = entries.reduce((sum, [, v]) => sum + v, 0);

    // If no data, return empty dataset
    if (total === 0) {
      return { labels: [], datasets: [] };
    }

    // Desc by count, then by version name for consistency
    entries.sort(([labelA, a], [labelB, b]) => {
      if (b !== a) return b - a; // Sort by count desc
      return labelA.localeCompare(labelB, undefined, { numeric: true }); // Then by version
    });

    const slices: { label: string; value: number }[] = [];
    let otherTotal = 0;

    entries.forEach(([label, value], idx) => {
      const pct = (value / total) * 100;
      const exceedsSliceLimit = idx >= max;
      const belowMin = minPct > 0 && pct < minPct;
      // pinnedLabel (typically the latest GitLab release tag) bypasses both
      // collapse rules so a freshly cut version with only a few deployments
      // still gets its own slice.
      const isPinned = !!pinned && label === pinned;
      if (!isPinned && (exceedsSliceLimit || belowMin)) {
        otherTotal += value;
      } else {
        slices.push({ label, value });
      }
    });

    if (otherTotal > 0) {
      slices.push({ label: 'Other', value: otherTotal });
    }

    const labels = slices.map((s) => s.label);
    const data = slices.map((s) => s.value);
    const bg = palette.slice(0, slices.length);
    return {
      labels,
      datasets: [
        {
          data,
          backgroundColor: bg,
          hoverBackgroundColor: bg.map((c) => lighten(c, 0.25)),
          borderWidth: 2,
          borderColor: '#fff',
          hoverBorderWidth: 4,
          hoverBorderColor: '#fff',
          hoverOffset: 12,
          borderRadius: 0,
        },
      ],
    };
  };

  /* --------------------------------------------------------------
   * Chart options (animation here at top level, not under hover)
   * -------------------------------------------------------------- */
  const options: ChartOptions<'pie'> = useMemo(() => {
    const opts: ChartOptions<'pie'> = {
      maintainAspectRatio: true,
      responsive: true,
      aspectRatio: 1,
      layout: {
        padding: { top: 30, bottom: 30, left: 30, right: 30 },
      },
      onHover: (event: any, activeElements: any) => {
        event.native.target.style.cursor = activeElements?.length > 0 ? 'pointer' : 'default';
      },
      plugins: {
        datalabels: {
          display: true,
          color: '#fff',
          font: {
            weight: 'bold',
            size: 12,
          },
          formatter: (value: number, ctx: any) => {
            const label = ctx.chart.data.labels[ctx.dataIndex];
            // Only show label if slice is large enough
            const sum = ctx.dataset.data.reduce((a: number, b: number) => a + b, 0);
            const percentage = ((value / sum) * 100);
            if (percentage > 3) {
              // Show version label for slices > 3%
              return label?.toString().split(':')[0] || '';
            }
            return '';
          },
          anchor: 'center' as const,
          align: 'center' as const,
          offset: 0,
        },
        legend: {
          display: false, // We'll create a custom legend outside
        },
        tooltip: {
          enabled: true,
          backgroundColor: 'rgba(30, 30, 46, 0.95)',
          titleFont: { size: 14, weight: 'bold' },
          bodyFont: { size: 12 },
          padding: 15,
          cornerRadius: 10,
          displayColors: true,
          borderColor: 'rgba(102, 126, 234, 0.3)',
          borderWidth: 1,
          callbacks: {
            title: () => {
              return 'Version Details';
            },
            label: (ctx: TooltipItem<'pie'>) => {
              const dataset = ctx.dataset;
              const raw = dataset.data[ctx.dataIndex] as number;
              const total = (dataset.data as number[]).reduce((s, v) => s + v, 0);
              const pct = total ? ((raw / total) * 100).toFixed(2) : '0.00';
              const label = ctx.label || 'Unknown';
              return [
                `Version: ${label}`,
                `Applications: ${raw}`,
                `Percentage: ${pct}%`,
                `Platform: ${dataType.toUpperCase()}`,
                `Total Apps: ${total}`
              ];
            },
            afterLabel: () => {
              return '─────────────';
            },
          },
        },
      },
    };
    return opts;
  }, [dataType]);

  /* --------------------------------------------------------------
   * Calculate total apps for display - MUST be before any conditional returns
   * -------------------------------------------------------------- */
  const totalApps = useMemo(() => {
    const sourceCounts = dataType === 'android' ? versionCounts.android_version : versionCounts.ios_version;
    return Object.values(sourceCounts).reduce((sum, count) => sum + count, 0);
  }, [dataType, versionCounts]);

  /* --------------------------------------------------------------
   * Build chart when inputs change
   * -------------------------------------------------------------- */
  useEffect(() => {
    try {
      const sourceCounts =
        dataType === 'android' ? versionCounts.android_version : versionCounts.ios_version;

      // Check if we have any data to display
      if (Object.keys(sourceCounts).length === 0) {
        // Empty-state — the "No Data" label below already conveys this to
        // the user. No need to spam the console on every render.
        setChartData({
          labels: ['No Data'],
          datasets: [{
            data: [1],
            backgroundColor: ['#e0e0e0'],
            borderWidth: 0,
          }],
        });
        setError(`No ${dataType} apps found`);
      } else {
        const chart = buildChartData(sourceCounts);
        setChartData(chart);
        setError(null);
      }
    } catch (err) {
      console.error('PieChart error:', err);
      setError('Failed to render chart');
    } finally {
      setLoading(false);
      onLoaded?.();
    }
  }, [dataType, versionCounts, onLoaded, props.pinnedLabel]);


  /* --------------------------------------------------------------
   * Render
   * -------------------------------------------------------------- */
  if (loading) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          minHeight: 120,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
        }}
      >
        <CircularProgress size={24} />
        <Typography variant="caption" sx={{ mt: 1 }}>
          Loading {dataType} data…
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          minHeight: 400,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
        }}
      >
        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
          {dataType === 'android' ? 'Android' : 'iOS'}
        </Typography>
        <Typography variant="body2" color="text.disabled">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-start',
      p: 2,
    }}>
      {/* Total Apps Count */}
      {totalApps > 0 && (
        <Typography
          variant="h6"
          sx={{
            mb: 2,
            color: 'text.secondary',
            fontWeight: 500,
            textAlign: 'center'
          }}
        >
          Total: {totalApps} {dataType === 'android' ? 'Android' : 'iOS'} App{totalApps !== 1 ? 's' : ''}
        </Typography>
      )}

      <Box sx={{
        width: '100%',
        maxWidth: '580px',
        height: 'auto',
        aspectRatio: '1',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        padding: 3,
        background: 'linear-gradient(145deg, #ffffff, #f3f3f3)',
        borderRadius: '50%',
        boxShadow: `
          20px 20px 60px #d9d9d9,
          -20px -20px 60px #ffffff,
          inset 2px 2px 5px rgba(255,255,255,0.9),
          inset -2px -2px 5px rgba(0,0,0,0.1)
        `,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: -3,
          left: -3,
          right: -3,
          bottom: -3,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          zIndex: -1,
          opacity: 0.15,
        },
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        '&:hover': {
          transform: 'scale(1.02)',
          boxShadow: `
            25px 25px 75px #d9d9d9,
            -25px -25px 75px #ffffff,
            inset 3px 3px 7px rgba(255,255,255,0.9),
            inset -3px -3px 7px rgba(0,0,0,0.15)
          `,
        },
      }}>
        <Pie
          ref={chartRef}
          data={chartData}
          options={options}
        />
      </Box>

      {/* Custom Legend Outside Circle */}
      <Box sx={{
        mt: 3,
        width: '100%',
        maxWidth: '500px',
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        gap: 1.5,
        px: 2,
      }}>
        {chartData.labels?.map((label, index) => {
          const dataset = chartData.datasets[0];
          const value = dataset.data[index] as number;
          const total = (dataset.data as number[]).reduce((s, v) => s + v, 0);
          const percentage = total ? ((value / total) * 100).toFixed(1) : '0.0';
          const labelText = String(label); // Ensure label is a string
          const color = Array.isArray(dataset.backgroundColor)
            ? dataset.backgroundColor[index]
            : '#ccc';

          return (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                px: 1,
                py: 0.5,
                borderRadius: 1,
                transition: 'all 0.2s',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.05)',
                  transform: 'translateY(-1px)',
                },
              }}
            >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  backgroundColor: color,
                  borderRadius: '2px',
                  border: '1px solid rgba(0,0,0,0.1)',
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  fontSize: '11px',
                  fontWeight: 500,
                  color: 'text.secondary',
                }}
              >
                {labelText}: {percentage}% ({value})
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

/* ------------------------------------------------------------------
 * Simple hex lighten helper
 * ------------------------------------------------------------------ */
function lighten(hex: string, amount = 0.2): string {
  if (!/^#?[0-9A-Fa-f]{6}$/.test(hex)) return hex;
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  const nr = Math.min(255, Math.round(r + (255 - r) * amount));
  const ng = Math.min(255, Math.round(g + (255 - g) * amount));
  const nb = Math.min(255, Math.round(b + (255 - b) * amount));
  return `#${nr.toString(16).padStart(2, '0')}${ng.toString(16).padStart(2, '0')}${nb
    .toString(16)
    .padStart(2, '0')}`;
}
